import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { threadApi } from "@/lib/api";
import type { Message, MessageCreate } from "@/lib/types";

export const threadKeys = {
  all: (requestId: string) => ["thread", requestId] as const,
};

/** Messages per page: the thread opens on the latest page. */
export const THREAD_PAGE = 50;
/** The API's largest page; a refetch reloads at most this many. */
const MAX_PAGE = 500;

export function useThread(requestId: string) {
  const queryClient = useQueryClient();
  const key = threadKeys.all(requestId);
  const [reachedStart, setReachedStart] = useState(false);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  useEffect(() => setReachedStart(false), [requestId]);

  const query = useQuery({
    queryKey: key,
    // A refetch keeps the earlier pages the user already loaded.
    queryFn: () => {
      const loaded = queryClient.getQueryData<Message[]>(key)?.length ?? 0;
      return threadApi.list(requestId, {
        limit: Math.min(Math.max(THREAD_PAGE, loaded), MAX_PAGE),
      });
    },
    enabled: !!requestId,
    // No refetchInterval — SSE handles real-time updates
    staleTime: 60_000,
  });

  const messages = query.data;
  const hasEarlier = !reachedStart && (messages?.length ?? 0) >= THREAD_PAGE;

  /** Load the page before the oldest message. Resolves to whether any loaded. */
  const loadEarlier = useCallback(async (): Promise<boolean> => {
    const current = queryClient.getQueryData<Message[]>(key) ?? [];
    if (!current.length) return false;
    const oldest = current.reduce((a, b) =>
      new Date(a.created_at) <= new Date(b.created_at) ? a : b,
    );
    setLoadingEarlier(true);
    try {
      const older = await threadApi.list(requestId, {
        limit: THREAD_PAGE,
        before: oldest.id,
      });
      if (older.length < THREAD_PAGE) setReachedStart(true);
      queryClient.setQueryData<Message[]>(key, (prev = []) => {
        const seen = new Set(prev.map((m) => m.id));
        return [...older.filter((m) => !seen.has(m.id)), ...prev];
      });
      return older.length > 0;
    } finally {
      setLoadingEarlier(false);
    }
  }, [queryClient, requestId]);

  return { ...query, hasEarlier, loadEarlier, loadingEarlier };
}

export function usePostMessage(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation<Message, Error, MessageCreate>({
    mutationFn: ({ images, ...payload }) =>
      images?.length
        ? threadApi.postWithImages(requestId, payload, images)
        : threadApi.post(requestId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: threadKeys.all(requestId),
      });
    },
  });
}

export function useMarkRead(requestId: string) {
  return useMutation<void, Error, string>({
    mutationFn: (messageId) => threadApi.markRead(requestId, messageId),
  });
}
